export default {
  common: {
    error: "Something went wrong",
    reload: "Reload",
    errorNotification: "We have been notified and will fix it as soon as possible. If the problem persists, please",
    contactUs: "Contact Us.",
    language: "Language",
    loading: "Loading...",
    loadingText: "Loading",
    save: "Save",
    savedSuccessfully: "Saved Successfully",
    saving: "Saving...",
    cancel: "Cancel",
    edit: "Edit",
    delete: "Delete",
    add: "Add",
    search: "Search",
    filter: "Filter",
    clear: "Clear",
    preview: "Preview",
    default: "Default: {{value}}",
    optional: "Optional",
    required: "Required",
    noData: "No data",
  },
  // Places navigation
  places: {
    navigation: {
      locations: "Locations",
      cities: "Cities", 
      regions: "Regions",
      countries: "Countries",
      showOnMap: "Show on map",
      addLocation: "Add location"
    },
    welcome: {
      title: "Welcome to Locations So Super.",
      subtitle: "Select a section from the navigation to get started."
    }
  },

  // Cities section
  cities: {
    title: "Cities",
    resourceSingular: "city",
    resourcePlural: "cities",
    headings: {
      name: "Name",
      status: "Status",
      locations: "Locations", 
      region: "Region",
      country: "Country"
    },
    breadcrumbs: {
      cities: "Cities"
    }
  },

  // Countries section  
  countries: {
    title: "Countries",
    resourceSingular: "country", 
    resourcePlural: "countries",
    headings: {
      name: "Name",
      status: "Status",
      locations: "Locations",
      cities: "Cities",
      regions: "Regions"
    },
    breadcrumbs: {
      countries: "Countries"
    }
  },

  // Regions section
  regions: {
    title: "Regions",
    resourceSingular: "region",
    resourcePlural: "regions", 
    headings: {
      name: "Name",
      status: "Status",
      locations: "Locations",
      cities: "Cities",
      country: "Country"
    },
    breadcrumbs: {
      regions: "Regions"
    }
  },

  // Map section
  map: {
    title: "Map",
    breadcrumbs: {
      locations: "Locations"
    }
  },

  // Import section
  import: {
    title: "CSV Import",
    breadcrumbs: {
      locations: "Locations"
    },
    new: {
      title: "Import locations by CSV",
      description: "Download a sample CSV template to see an example of the format required",
      sampleTemplate: "sample CSV template",
      uploadAndContinue: "Upload and continue",
      abortOnError: "Abort on error",
      abortOnErrorHelp: "If there is an error, the import will abort, display the error and no locations will be imported.",
      needHelp: "Need help importing locations?"
    },
    show: {
      title: "CSV import",
      youCanLeavePage: "You can leave this page",
      backgroundMessage: "Your import will still run in the background",
      importProgress: "Import progress",
      imported: "Imported",
      suffix: "locations",
      importLog: "Import log",
      cancelImport: "Cancel Import",
      cancelConfirmation: {
        title: "Cancel Import",
        message: "Are you sure you want to cancel this import job? This action cannot be undone.",
        note: "Note: For in progress jobs, the job will need to complete and it will be deleted right after.",
        yesCancel: "Yes, cancel import",
        noContinue: "No, continue import"
      },
      report: "Report",
      failedLocations: "Failed locations",
      uploadAnother: "Upload another CSV",
      backToLocations: "Back to locations",
      importSucceeded: "Import succeeded",
      importFailed: "Import failed"
    },
    messages: {
      uploadSuccess: "Successfully uploaded CSV",
      uploadError: "Couldn't upload CSV",
      pending: "Import hasn't started yet",
      inProgress: "Import in progress",
      completed: "Import completed",
      invalidHeader: "The following columns are required",
      invalidCsvFile: "Invalid CSV file",
      aborted: "Import aborted",
      failed: "Import failed",
      cancelled: "Import cancelled"
    },
    errors: {
      csvTooLarge: "CSV is too large",
      languageNotEnabled: "Language '{{language}}' is not enabled for this shop. Please enable it in Settings > Languages first.",
      fieldNotTranslatable: "Field '{{field}}' is not translatable. Translatable fields are: {{fields}}",
      locationError: "Location with slug: {{slug}} - {{error}}",
      planLimit: "You are at your plan limit for locations"
    }
  },

  // Location form
  location: {
    index: {
      title: "Locations",
      addLocation: "Add location",
      import: "Import",
      noPrioritySet: "No priority set",
      published: "Published",
      draft: "Draft",
      resourceSingular: "location",
      resourcePlural: "locations",
      headings: {
        name: "Name",
        status: "Status", 
        city: "City",
        region: "Region",
        country: "Country",
        priority: "Priority"
      }
    },
    form: {
      title: "Add location",
      editTitle: "Edit location",
      basicInformation: "Basic information",
      name: "Name",
      namePlaceholder: "e.g. Brians Bricks",
      nameHelpDefault: "Default: {{defaultName}}",
      details: "Details",
      description: "Description",
      descriptionPlaceholder: "Tell customers what makes this location special",
      address: "Address",
      addressPlaceholder: "123 Main Street, City, Country",
      addressLine1: "Address line 1",
      addressLine2: "Address line 2",
      postalZipCode: "Postal / ZIP code",
      city: "City",
      stateProvinceRegion: "State / Province / Region",
      country: "Country",
      phone: "Phone",
      phonePlaceholder: "+1 (555) 123-4567",
      email: "Email", 
      emailPlaceholder: "store@example.com",
      website: "Website",
      websitePlaceholder: "https://www.example.com",
      image: "Image",
      imagePlaceholder: "Enter image URL",
      status: "Status",
      statusActive: "Active",
      statusInactive: "Inactive",
      statusDraft: "Draft",
      priority: "Priority",
      priorityHelpText: "Higher priority locations appear first in search results",
      findOnMapButton: "Find on map"
    },
    validation: {
      addressNotFound: "Could not find address",
      createSuccess: "Location created successfully", 
      updateSuccess: "Location updated successfully",
      checkFields: "Please check your fields",
      findOnMap: "Please find your location on the map before saving"
    },
    errors: {
      loading: "Error loading location"
    },
    actions: {
      save: "Save location",
      delete: "Delete location",
      preview: "Preview"
    },
    customFields: {
      title: "Custom fields",
      noCustomFields: "No custom fields configured",
      configureFields: "Configure fields",
      emptyState: "You haven't configured any custom fields yet. Custom fields allow you to add additional information specific to your business needs."
    },
    openingHours: {
      title: "Opening hours",
      checkboxLabel: "This location has opening and closing times",
      statusOpen: "Open",
      statusOpen24h: "Open 24H",
      statusClosed: "Closed",
      openingTimeLabel: "Opening time",
      closingTimeLabel: "Closing time",
      days: {
        monday: "Monday",
        tuesday: "Tuesday", 
        wednesday: "Wednesday",
        thursday: "Thursday",
        friday: "Friday",
        saturday: "Saturday",
        sunday: "Sunday"
      }
    },
    table: {
      sortOptions: {
        recentlyAdded: "Recently added",
        recentlyModified: "Recently modified"
      },
      filters: {
        status: "Status",
        taggedWith: "Tagged with",
        locationStatus: "Location status",
        noTagsToShow: "No tags to show",
        searchPlaceholder: "Search"
      },
      bulkActions: {
        setPublished: "Set as published",
        setUnpublished: "Set as unpublished", 
        setPriority: "Set priority",
        deleteLocations: "Delete locations",
        exportCSV: "Export as CSV"
      },
      appliedFilters: {
        statusIs: "Status is {{status}}",
        taggedWith: "Tagged with {{tag}}",
        nameIncludes: "Name includes {{search}}"
      },
      modals: {
        deleteTitle: "Delete locations",
        deleteMessage: "Are you sure you want to delete {{count}}?",
        deleteMessageAll: "Are you sure you want to delete all locations?",
        prioritiseTitle: "Prioritise {{count}} locations",
        prioritiseTitleAll: "Prioritise all locations",
        delete: "Delete",
        cancel: "Cancel"
      },
      emptyState: {
        noResultsHeading: "Couldn't find any {{resourcePlural}}",
        noResultsText: "Try changing the filters or search term",
        noDataHeading: "Add a location to populate {{resourcePlural}}",
        addLocation: "Add Location",
        importFromCSV: "Import from CSV"
      },
      pagination: {
        label: "Page {{page}} of {{pages}}"
      },
      messages: {
        updatedSuccessfully: "Updated successfully",
        deletedSuccessfully: "Deleted successfully", 
        exportedSuccessfully: "Exported successfully",
        error: "Something went wrong"
      }
    }
  },

  // SEO Editor
  seo: {
    title: "Page content",
    editTemplate: "Edit SEO template", 
    pageTitle: "Title",
    pageDescription: "Description",
    metaTitle: "Title",
    metaDescription: "Description",
    urlHandle: "URL handle",
    searchEnginePreview: "Search engine listing preview",
    editMeta: "Edit Meta",
    previewMeta: "Preview Meta",
    loading: "Loading...",
    editingLocaleHelp: "Editing {{locale}} content."
  },

  // SEO Templates section
  seoTemplates: {
    title: "SEO templates",
    breadcrumbs: {
      seoTemplates: "SEO templates"
    },
    template: {
      title: "{{templateName}} template - {{locale}}",
      discardChanges: "Discard changes"
    },
    form: {
      pageContent: "Page content",
      searchEnginePreview: "Search engine listing preview",
      title: "Title",
      description: "Description",
      pageTitle: "Page title",
      metaDescription: "Meta description",
      editingLocaleHelp: "Editing {{locale}} translations. Empty fields will display content from {{defaultLocale}}.",
      defaultValue: "Default: {{value}}"
    },
    language: {
      label: "Language",
      defaultHelp: "This is your default language",
      editingHelp: "Editing {{locale}} translations. Empty fields will show {{defaultLocale}} content."
    },
    banner: {
      multipleLanguagesDisabled: "Multiple languages not enabled",
      enableInSettings: "Enable in Settings",
      multipleLanguagesInfo: "To manage SEO templates for multiple languages, enable internationalization in your language settings. Currently showing {{locale}} templates only."
    },
    badges: {
      errors: "Errors!",
      unsaved: "Unsaved"
    },
    messages: {
      saveSuccess: "SEO templates saved successfully",
      saveError: "Couldn't save templates",
      error: "Error..."
    }
  },

  // Area form
  area: {
    form: {
      basicInformation: "Basic information",
      name: "Name",
      save: "Save",
      saving: "Saving...",
      cancel: "Cancel",
      yourLocationsIn: "Your locations in {{name}}",
      tableHeadings: {
        name: "Name",
        status: "Status",
        city: "City",
        region: "Region",
        country: "Country"
      },
      resourceName: {
        singular: "{{name}} location",
        plural: "locations in {{name}}"
      }
    }
  },

  // Settings section
  settings: {
    navigation: {
      map: "Map",
      tags: "Tags",
      customFields: "Custom fields",
      languages: "Languages",
      plan: "Plan",
      addTag: "Add tag",
      addField: "Add field"
    },
    planBadges: {
      trial: "!",
      endsSoon: "Ends soon"
    },
    common: {
      loading: "Loading...",
      error: "Error...",
      breadcrumbs: {
        settings: "Settings"
      },
      skeleton: {
        customFields: "Custom fields",
        addField: "Add field",
        name: "Name",
        contentType: "Content type",
        usedIn: "Used in",
        field: "Field",
        fields: "Fields"
      },
      messages: {
        savedSuccessfully: "{{setting}} settings saved successfully",
        couldntSave: "Couldn't save {{setting}} settings"
      }
    },
    map: {
      title: "Map",
      messages: {
        savedSuccessfully: "Map settings saved successfully",
        couldntSave: "Couldn't save settings"
      },
      mapboxToken: {
        title: "Mapbox token",
        description: "Enter your Mapbox token to power your store locator",
        label: "Mapbox token",
        placeholder: "Enter token here to continue using your map.",
        placeholderManaged: "Enter token here to continue using your map once you run out of free map loads.",
        helpText: "Follow our instructions for creating a Mapbox key, then paste your key above. If you run into any issues, please get in touch for assistance.",
        linkInstructions: "instructions for creating a Mapbox key",
        linkSupport: "get in touch for assistance"
      },
      managedToken: {
        title: "Map loads",
        description: "You have up to {{max}} free map loads with a managed token, after that you will have to create your own token."
      },
      appearance: {
        title: "Appearance",
        description: "Change the visuals of your map"
      },
      mapStyle: {
        title: "Map style",
        options: {
          streets: "Streets",
          outdoors: "Outdoors", 
          light: "Light",
          dark: "Dark",
          satellite: "Satellite",
          satelliteStreets: "Satellite Streets",
          navigationDay: "Navigation Day",
          navigationNight: "Navigation Night"
        }
      },
      searchBehaviour: {
        title: "Search behaviour",
        description: "Controls how visitors find locations on your map.",
        geolocateLabel: "Automatically estimate the visitor's location and show nearby stores",
        geolocateHelp: "If geolocation succeeds, the map will move to focus near the visitor. If an estimated location can't be found, the map will remain at the starting position.",
        filterBehaviour: {
          title: "Filter behaviour",
          all: {
            label: "All tags",
            help: "For a location to show up in filters, it must have all the selected tags"
          },
          any: {
            label: "Any tags", 
            help: "For a location to show up in filters, it can have any of the selected tags"
          }
        },
        keepSearchInView: {
          label: "Keep search marker in view",
          help: "When search results appear, the map will zoom out to show all matching locations"
        },
        distanceUnits: {
          title: "Distance units",
          km: "Kilometers",
          miles: "Miles"
        },
        maxResults: {
          label: "Max results",
          help: "Limit the number of locations shown in search results",
          options: {
            results30: "30 results"
          }
        },
        maxDistance: {
          label: "Max distance", 
          help: "Only show locations within this distance from the search point",
          options: {
            unlimited: "Unlimited"
          }
        },
        showLocationNames: {
          label: "Show location names in suggestions",
          help: "Display location names in search suggestions"
        },
        sortByPriority: {
          label: "Sort by priority instead of distance",
          help: "It is highly recommended to set a maximum distance (that is not Unlimited) if this box is checked. Otherwise your customers will see far away locations that have a high priority listed above those that are within a reasonable distance from them (or their search query, more specifically)."
        }
      },
      searchResults: {
        title: "Search results",
        description: "Controls how locations are returned on your map.",
        locationsLabel: "Search results text",
        locationsHelp: "Text shown above search results (e.g., 'Showing 5 locations')",
        prefixLabel: "Search results prefix", 
        prefixHelp: "Text shown before the number of results"
      },
      popups: {
        title: "Popups",
        showPopups: {
          label: "Show popups",
          help: "Display location details when visitors click on map markers"
        }
             },
       whitelabel: {
         title: "Whitelabel",
         hideLabel: "Hide powered by text",
         hideHelp: "Remove our branding from your store locator"
       }
    },
    languages: {
      title: "Languages",
      error: "Error...",
      messages: {
        savedSuccessfully: "Language settings saved successfully",
        couldntSave: "Couldn't save language settings"
      },
      banner: {
        upgradeTitle: "Multi-language support requires plan upgrade",
        upgradeAction: "Upgrade to Superest",
        upgradeMessage: "You can still change your default language, but enabling multiple languages requires an upgrade.",
        disabledMessage: "Multiple language settings are disabled on your current plan"
      },
      defaultLanguage: {
        title: "Default Language", 
        description: "Choose your primary language that your content will be displayed in.",
        label: "Default language",
        help: "This language will be used when no specific language is requested"
      },
      internationalization: {
        title: "Internationalization",
        description: "Enable multiple languages. Reach more customers and boost your SEO.",
        enableLabel: "Enable multiple languages",
        enableHelp: "Allow customers to view your store locator in different languages"
      },
      supportedLanguages: {
        title: "Additional Supported Languages",
        description: "Select additional languages you want to support. Once you've enabled a language, you'll see the option to provide translations throughout the app. For supported languages, the default SEO templates, as well as the common variable themes will get generated automatically.",
        defaultLocaleNote: "This is your default language and cannot be disabled",
        shopifyEnabled: {
          label: "This language is enabled in my Shopify Store",
          importantNote: "Important:",
          description: "Uncheck the above box if this language is not enabled and published for your Shopify store. It's recommended to enable the language for your Shopify store first by going to Settings > Languages in your Shopify admin if you haven't already for a smoother customer experience."
        },
        urlFormats: {
          cleanUrls: "Clean URLs:",
          betterSeo: "Better SEO and user experience",
          queryUrls: "Query parameter URLs:",
          considerEnabling: "Consider enabling in Shopify for better URLs"
        },
        preview: {
          button: "Preview",
          tooltip: "Preview {{language}} store locator"
        },
        tooltips: {
          noTemplates: "No default SEO templates available for this language. You can create custom translations easily."
        }
      }
    },
    tags: {
      title: "Tags",
      addTag: "Add tag",
      error: "Error",
      messages: {
        deletedSuccessfully: "Tags deleted successfully",
        couldntDelete: "Tags couldn't be deleted",
        savedSuccessfully: "Tag saved successfully",
        couldntSave: "Please check your fields",
        tagDeleted: "Tag deleted successfully"
      },
      resourceName: {
        singular: "tag",
        plural: "tags"
      },
      filters: {
        anyGroup: "Any group",
        inGroup: "In group",
        tagGroup: "Tag group"
      },
      appliedFilters: {
        inGroup: "In {{group}} group",
        nameContains: "Tag name contains {{query}}"
      },
      bulkActions: {
        deleteTag: "Delete tag",
        deleteTags: "Delete tags"
      },
      form: {
        language: "Language",
        title: "Title",
        description: "Description",
        group: "Group",
        groupPlaceholder: "Select group",
        addGroup: "Add",
        editingTranslations: "Editing {{language}} translations. Empty fields will display content from {{defaultLanguage}}.",
        defaultValue: "Default: {{value}}",
        image: "Image",
        imageUrl: "Image URL"
      },
      actions: {
        save: "Save",
        deleteTag: "Delete tag"
      },
      new: {
        title: "Add tag",
        messages: {
          createSuccess: "Tag created successfully",
          checkFields: "Please check your fields"
        },
        actions: {
          createTag: "Create tag"
        }
      }
    },
    customFields: {
      title: "Custom fields",
      addField: "Add field",
      error: "Error",
      resourceName: {
        singular: "Field",
        plural: "Fields"
      },
      headings: {
        name: "Name",
        contentType: "Content type",
        usedIn: "Used in"
      },
      emptyState: {
        heading: "Create and manage your custom fields",
        createAction: "Create field",
        description: "Use custom fields for unique attributes about your locations. These fields can be anything from social profiles to the store managers name and email"
      },
      pluralization: {
        location: "location",
        locations: "locations"
      },
      form: {
        name: "Name",
        contentType: "Content Type",
        key: "Key",
        keyPrefix: "location.fields.",
        keyHelp: "Use this key to access a location's {{name}} content in your liquid templates.",
        translations: {
          title: "Translations",
          showTranslations: "Show translations",
          hideTranslations: "Hide translations",
          description: "Translations are optional. Empty fields will display the {{language}} field name.",
          nameTranslation: "Name ({{language}})",
          defaultValue: "Default: {{value}}"
        }
      },
      messages: {
        savedSuccessfully: "Field saved successfully",
        couldntSave: "Field couldnt be saved",
        deletedSuccessfully: "Field deleted successfully",
        couldntDelete: "Field couldnt be deleted"
      },
      actions: {
        delete: "Delete"
      },
      new: {
        title: "Add field",
        messages: {
          savedSuccessfully: "Field saved successfully",
          couldntSave: "Field couldnt be saved"
        }
      },
      contentTypes: {
        text: "Text",
        textArea: "Text Area", 
        email: "Email",
        number: "Number",
        phoneNumber: "Phone number",
        time: "Time",
        url: "URL"
      }
    },
    plan: {
      title: "Plan",
      error: "Error",
      changePlan: "Change plan",
      usdPerMonth: "USD / month",
      sections: {
        yourPlanIncludes: "Your plan includes",
        nextBillingDate: "Next billing date",
        trial: "Trial"
      },
      banners: {
        planUpdated: "Your plan has been updated",
        thankYouUpgrading: "Thank you for upgrading!",
        letUsKnow: "Let us know",
        improvementText: "if theres anything we can do better."
      },
      locations: {
        upTo: "Up to",
        locations: "locations",
        unlimited: "Unlimited locations",
        used: "used",
        ofUsed: "{{used}} of {{max}} used"
      },
      trial: {
        daysLeft: "{{days}} days left",
        daysUsed: "{{used}} of {{total}}days used",
        selectPlan: "Select a plan",
        currentPlan: "Current Plan",
        trialBadge: "Trial",
        currentBadge: "Current",
        yourTrialJustStarted: "Your trial just started",
        trialEndsAfterToday: "Your trial ends after today",
        trialEndsTomorrow: "Your trial ends tomorrow",
        daysLeftInTrial: "{{days}} days left in your trial",
        continueAddingLocations: "Continue adding locations after {{date}} by selecting a plan",
        selectPlanToday: "Please select a plan today to continue adding locations"
      },
      footer: {
        description: "Manage or change your Locations So Super plan. View our",
        termsOfService: "terms of service",
        and: "and",
        privacyPolicy: "privacy policy"
      },
      features: {
        analytics: {
          title: "Analytics",
          description: "Find out how people are using your locations and where they are searching for"
        },
        internationalization: {
          title: "Multi-language support",
          description: "Boost SEO and reach more customers in target markets by enabling multiple languages"
        },
        customThemes: {
          editLiquid: {
            title: "Edit theme liquid templates",
            description: "Display location data in any way you like with access to theme liquid"
          },
          editStructuredData: {
            title: "Edit business structured data",
            description: "Edit how google understand pages by editing your structured data"
          }
        }
      },
      edit: {
        title: "Change plan",
        allPlansInclude: "All plans above include",
        needingMoreLocations: "Needing more locations?",
        footerQuestion: "Have any questions before you start?",
        sendMessage: "send us a message",
        allFeatures: {
          seoDirectory: {
            title: "SEO directory and landing pages",
            description: "Automatically create a SEO directory of all your locations, cities and regions"
          },
          mapPage: {
            title: "Map page with search and filter",
            description: "Search and filter all your locations with a on a map powered my mapbox"
          },
          tags: {
            title: "Tags",
            description: "Create tags with images and descriptions and group them"
          },
          openingHours: {
            title: "Opening hours",
            description: "Set and display the opening hours for your locations"
          },
          mapStyles: {
            title: "Map styles",
            description: "Change the look of the maps with a number of preset styles"
          },
          customFields: {
            title: "Custom fields",
            description: "Add any new fields you want, from social links to store manager"
          },
          csvUpload: {
            title: "Upload and export with CSV",
            description: "Upload and bulk edit your locations."
          },
          richSnippets: {
            title: "Rich snippets",
            description: "Help google understand pages better with local business structured data"
          },
          directionsAndCalls: {
            title: "Directions and calls",
            description: "Visitors can easily get directions and make calls to the locations"
          },
          languageSupport: {
            title: "Language Support",
            description: "Change default language to something other than English. Translation tempaltes provided for 32 major languages for easy setup."
          }
        }
      },
      planCard: {
        bestToCustomize: "Best to customize",
        currentPlan: "Current plan",
        chooseThisPlan: "Choose this plan",
        locationsSection: "Locations",
        featuresSection: "Features",
        upTo: "Up to",
        locations: "locations",
        tooltipTooManyLocations: "You have too many locations and/or enabled languages for this plan."
      },
      confirmation: {
        error: "Error confirming plan"
      }
    }
  },

  // Themes section
  themes: {
    title: "Themes",
    currentTheme: {
      title: "Current theme",
      description: "This is the theme customers see when they visit your location app."
    },
    themeLibrary: {
      title: "Theme library", 
      description: "Manage your location locations themes. Add and publish themes to change your online location's appearance."
    },
    skeleton: {
      currentThemeDescription: "This is the theme your customers see when they use your locations app."
    },
    modal: {
      overrideTitle: "Override current theme?",
      override: "Override",
      cancel: "Cancel",
      undoWarning: "This action can't be undone."
    },
    messages: {
      publishSuccess: "Theme published successfully",
      publishError: "Couldn't publish theme",
      error: "Something went wrong"
    },
    emptyStates: {
      settings: {
        heading: "This theme has no settings",
        description: "Create settings in the template editor.",
        createSettings: "Create settings",
        upgradePlan: "Upgrade plan",
        upgradeRequired: "Upgrade plan to create settings"
      },
      language: {
        heading: "This theme has no language terms",
        description: "Create language terms in the template editor.",
        createLanguageTerms: "Create language terms",
        upgradePlan: "Upgrade plan",
        upgradeRequired: "Upgrade plan to create language terms"
      }
    },
    templates: {
      categories: {
        templates: "Templates",
        schemas: "Schemas",
        styles: "Styles",
        liquidReference: "Liquid reference"
      },
      items: {
        landing: "Landing",
        directory: "Directory",
        country: "Country",
        region: "Region",
        city: "City",
        location: "Location",
        language: "Language",
        settings: "Settings",
        stylesheet: "Stylesheet"
      },
      errors: {
        errorsInTemplate: "Errors in template",
        error: "Error"
      },
      badges: {
        errors: "Errors!",
        unsaved: "Unsaved"
      },
      actions: {
        viewTheme: "View theme",
        discard: "Discard",
        save: "Save"
      }
    },
    language: {
      messages: {
        saveSuccess: "Theme language translations saved successfully",
        saveError: "Theme couldn't save"
      },
      form: {
        language: "Language",
        defaultLanguageHelp: "This is your default language",
        translationHelp: "Editing translations for {{selectedLocale}}. Empty fields will display content from {{defaultLocale}} (your default language)."
      },
      banner: {
        multipleLanguagesNotEnabled: {
          title: "Multiple languages not enabled",
          action: "Enable in Settings",
          description: "To manage translations for multiple languages, enable internationalization in your language settings. Currently showing {{locale}} translations only."
        },
        translating: {
          title: "Editing {{to}} translations",
          description: "Any field you leave empty will automatically display the {{defaultLocale}} content to your customers. Only translate the fields where you want different content in {{selectedLocale}}."
        }
      },
      accessLanguageTerms: "Access the {{category}} language terms in liquid with {{code}}",
      fields: {
        // Common language fields - add specific ones as needed
        title: "Title",
        description: "Description",
        heading: "Heading",
        subheading: "Subheading",
        button_text: "Button text",
        link_text: "Link text",
        placeholder: "Placeholder",
        label: "Label",
        help_text: "Help text",
        error_message: "Error message",
        success_message: "Success message",
        
        // Opening hours
        Monday: "Monday",
        Tuesday: "Tuesday",
        Wednesday: "Wednesday",
        Thursday: "Thursday",
        Friday: "Friday",
        Saturday: "Saturday",
        Sunday: "Sunday",
        opening_hours_format: "Opening hours format",
        closes_at_format: "Closes at format",
        opens_at_format: "Opens at format",
        opens_on: "Opens on",
        open_24h: "Open 24h",
        open: "Open",
        closed: "Closed",
        closes_at: "Closes at",
        opens_at: "Opens at",
        
        // Map
        search_map: "Search map",
        search_area: "Search this area",
        view_map: "Map",
        view_list: "List",
        open_filters: "Filters",
        stores_near_you: "Stores near you",
        header: "Find a Store!",
        search_placeholder: "Type a postcode or address...",
        no_listings: "We're sorry, we can't find any locations for your search. Try changing the filters or the search location",
        
        // Directory
        view_directory: "Store Directory",
        
        // Location
        get_directions: "Directions",
        call: "Call",
        view_website: "Website",
        email: "Email",
        opening_hours_header: "Opening Hours",
        description_header: "Information",
        phone_number_header: "Phone",
        address_header: "Address",
        tag_groups_to_display: "Tag groups to display"
      },
      categories: {
        // Common language categories - add specific ones as needed
        general: "General",
        navigation: "Navigation",
        forms: "Forms",
        buttons: "Buttons",
        messages: "Messages",
        errors: "Errors",
        opening_hours: "Opening Hours",
        map: "Map",
        directory: "Directory",
        location: "Location"
      }
    },
    settings: {
      messages: {
        saveSuccess: "Theme saved successfully",
        saveError: "Theme couldn't save"
      },
      accessSettings: "Access the {{category}} settings in liquid with {{code}}",
      fields: {
        // Colors
        color_text: "Color text",
        color_text_heading: "Color text heading", 
        color_background: "Color background",
        color_accent: "Color accent",
        color_text_positive: "Color text positive",
        color_text_negative: "Color text negative",
        dark_theme: "Dark theme",
        color_marker: "Color marker",
        color_search_marker: "Color search marker",
        
        // Typography
        base_font_size: "Base font size",
        
        // Buttons
        border_radius: "Border radius",
        
        // Images
        aspect_ratio: "Aspect ratio",
        fit: "Fit",
        
        // Spacing
        page_width: "Page width",
        page_width_small: "Page width small",
        gutter_desktop: "Gutter desktop",
        gutter_mobile: "Gutter mobile",
        desktop_vertical_spacing: "Desktop vertical spacing",
        mobile_vertical_spacing: "Mobile vertical spacing",
        
        // Breakpoints
        breakpoint_xs: "Breakpoint XS",
        breakpoint_sm: "Breakpoint SM",
        breakpoint_md: "Breakpoint MD",
        
        // Map
        search_on_move: "Search on move",
        scroll_to_zoom: "Scroll to zoom",
        
        // Landing page
        sidebar_width: "Sidebar width",
        sidebar_min_height: "Sidebar min height",
        marker_image_src: "Marker image source",
        show_filter_descriptions: "Show filter descriptions",
        
        // Location page
        tag_groups_to_display: "Tag groups to display",
        tag_groups_to_display_as_icons: "Tag groups to display as icons",
        show_related_locations: "Show related locations",
        
        // Locations
        hide_regions: "Hide regions"
      },
      categories: {
        colors: "Colors",
        typography: "Typography",
        buttons: "Buttons",
        images: "Images",
        spacing: "Spacing",
        breakpoints: "Breakpoints",
        map: "Map",
        landing_page: "Landing page",
        location_page: "Location page",
        locations: "Locations"
      }
    },
    navigation: {
      sublimeTheme: "Sublime Theme",
      settings: "Settings",
      language: "Language",
      templates: "Templates",
      changeTheme: "Change theme",
      viewDirectory: "View directory",
      viewLandingPage: "View landing page"
    },
    card: {
      lastUpdated: "Last updated",
      view: "View",
      customize: "Customize",
      publish: "Publish",
      actions: "Actions"
    }
  },

  // Analytics section
  analytics: {
    title: "Analytics",
    timePeriods: {
      last7days: "Last 7 days",
      last30days: "Last 30 days"
    },
    comparison: {
      compareToPrevious: "Compare to previous period",
      stopComparing: "Stop comparing to previous period"
    },
    sections: {
      searchLocations: "Search locations"
    },
    cards: {
      sessions: "Sessions",
      topSearches: "Top searches",
      topLandingPageType: "Top landing page type",
      topPageViewsBySession: "Top page views by session",
      topLocations: "Top locations",
      topReferrers: "Top referrers",
      searches: "Searches",
      topLandingPagesBySession: "Top landing pages by session"
    },
    chartLabels: {
      desktop: "Desktop"
    }
  },

}; 